import { useEffect } from 'react';
import { ActivityIndicator, AppState, View } from 'react-native';
import type { Href } from 'expo-router';
import { Redirect, useRouter } from 'expo-router';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Text } from '@/components/ui/primitives';
import { apiRequestJson } from '@/lib/queryClient';
import {
  getAuthHref,
  getLeagueIndexHref,
  getProfileHref,
} from '@/lib/routes';
import { StyleSheet } from 'react-native-unistyles';

const styles = StyleSheet.create((theme) => ({
  connectionActions: {
    alignItems: 'stretch',
    alignSelf: 'stretch',
    gap: theme.spacing.s,
    marginTop: theme.spacing.s,
    maxWidth: 360,
    width: '100%',
  },
  connectionError: {
    alignItems: 'center',
    backgroundColor: theme.colors.background,
    flex: 1,
    gap: theme.spacing.m,
    justifyContent: 'center',
    paddingHorizontal: theme.spacing.l,
  },
  loading: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
}));

export function RouteGateLoadingState() {
  return (
    <View style={styles.loading}>
      <ActivityIndicator size="large" />
    </View>
  );
}

/**
 * Shown when the session check could not reach the API. A stored session may still be valid,
 * so this replaces the signed-out landing/sign-in redirect until a retry gets an answer.
 */
export function RouteGateConnectionErrorState({ onRetry }: { onRetry: () => void }) {
  const router = useRouter();

  useEffect(() => {
    const subscription = AppState.addEventListener('change', (state) => {
      if (state === 'active') {
        onRetry();
      }
    });
    return () => subscription.remove();
  }, [onRetry]);

  return (
    <View style={styles.connectionError} testID="auth-connection-error">
      <Text variant="caption" color="mutedForeground" textTransform="uppercase" letterSpacing={1}>
        Connection
      </Text>
      <Text variant="title" color="text" textAlign="center">
        Can&apos;t reach PCKM
      </Text>
      <Text color="mutedForeground" textAlign="center">
        Check your connection, or turn off any VPN or DNS filter, then try again.
      </Text>
      <View style={styles.connectionActions}>
        <Button size="lg" onPress={onRetry} accessibilityLabel="Try again">
          <Text variant="button" color="primaryForeground">
            Try again
          </Text>
        </Button>
        <Button variant="ghost" onPress={() => router.push(getAuthHref())} accessibilityLabel="Go to sign in">
          <Text variant="button" color="mutedForeground">
            Go to sign in
          </Text>
        </Button>
      </View>
    </View>
  );
}

type RouteGateProps = {
  children: React.ReactNode;
  fallbackHref?: Href;
  isAllowed: boolean;
  isLoading?: boolean;
};

const RouteGate = ({ children, fallbackHref = getProfileHref(), isAllowed, isLoading = false }: RouteGateProps) => {
  const { isAuthenticated, isLoading: isAuthLoading, hasConnectionError, retry } = useAuth();

  if (isAuthLoading || isLoading) {
    return <RouteGateLoadingState />;
  }

  if (hasConnectionError) {
    return <RouteGateConnectionErrorState onRetry={retry} />;
  }

  if (!isAuthenticated) {
    return <Redirect href={getAuthHref()} />;
  }

  if (!isAllowed) {
    return <Redirect href={fallbackHref} />;
  }

  return <>{children}</>;
};

type AdminRouteGateProps = {
  children: React.ReactNode;
  requireBetsAdmin?: boolean;
};

export const AdminRouteGate = ({ children, requireBetsAdmin = false }: AdminRouteGateProps) => {
  const { user, isLoading } = useAuth();
  const isAllowed = requireBetsAdmin ? Boolean(user?.isAdmin || user?.isBetsAdmin) : Boolean(user?.isAdmin);
  return (
    <RouteGate isAllowed={isAllowed} isLoading={isLoading}>
      {children}
    </RouteGate>
  );
};

type LeagueManagerRouteGateProps = {
  children: React.ReactNode;
  leagueId: string;
};

export const LeagueManagerRouteGate = ({ children, leagueId }: LeagueManagerRouteGateProps) => {
  const { data: permissions, isLoading } = useQuery<{
    isOwner: boolean;
    isManager: boolean;
  }>({
    queryKey: ['/api/leagues', leagueId, 'permissions'],
    enabled: leagueId.length > 0,
    queryFn: async () =>
      apiRequestJson<{ isOwner: boolean; isManager: boolean }>(`/api/leagues/${leagueId}/permissions`, 'GET'),
  });

  return (
    <RouteGate
      isAllowed={Boolean(permissions?.isOwner || permissions?.isManager)}
      isLoading={leagueId.length > 0 && isLoading}
      fallbackHref={getLeagueIndexHref(leagueId)}
    >
      {children}
    </RouteGate>
  );
};
